import { BASE_URL } from '../../consts/baseUrl';
import { authFetch } from '../../utils/authUtils';
import { debugWarn } from '../../utils/debug';
import {
  LoginRequest,
  LoginResponse,
  RefreshResponse,
} from '../../modules/auth/interfaces';

const AUTH_BASE = '/api/auth';

/**
 * Effettua login utente.
 *
 * @param credentials - Email e password
 * @returns Token e informazioni utente
 * @throws Error se credenziali non valide o errore di rete
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${BASE_URL}${AUTH_BASE}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || getErrorMessage(response.status);
    throw new Error(message);
  }

  return response.json();
}

/**
 * Rinnova access token usando refresh token.
 * Se companyId è fornito, viene incluso nel nuovo access token per preservare il contesto company.
 *
 * @param refreshToken - Refresh token corrente
 * @param companyId - ID company selezionata (opzionale, per mantenere il contesto)
 * @returns Nuovi token
 * @throws Error se refresh token non valido o scaduto
 */
export async function refresh(refreshToken: string, companyId?: string): Promise<RefreshResponse> {
  const response = await fetch(`${BASE_URL}${AUTH_BASE}/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${refreshToken}`,
    },
    ...(companyId ? { body: JSON.stringify({ companyId }) } : {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || getErrorMessage(response.status);
    throw new Error(message);
  }

  return response.json();
}

/**
 * Richiede invio email per reset password (forgot password).
 *
 * @param email - Email dell'utente
 * @throws Error se email non valida, utente non trovato, o errore di rete
 */
export async function forgotPassword(email: string): Promise<void> {
  const response = await fetch(`${BASE_URL}${AUTH_BASE}/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || getErrorMessage(response.status);
    throw new Error(message);
  }
}

/**
 * Resetta la password usando il token ricevuto via email.
 *
 * @param params - Token, nuova password e conferma
 * @throws Error se token non valido/scaduto o password non conforme
 */
export async function resetPassword(params: {
  token: string;
  newPassword: string;
  confirmNewPassword: string;
}): Promise<void> {
  const response = await fetch(`${BASE_URL}${AUTH_BASE}/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || getErrorMessage(response.status);
    throw new Error(message);
  }
}

/**
 * Cambia password utente loggato.
 *
 * @param accessToken - Access token JWT
 * @param params - Password attuale, nuova password e conferma
 * @throws Error se password errata o non conforme
 */
export async function changePassword(
  accessToken: string,
  params: {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
  }
): Promise<void> {
  const response = await authFetch(`${BASE_URL}${AUTH_BASE}/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || getErrorMessage(response.status);
    throw new Error(message);
  }
}

/**
 * Effettua logout utente.
 *
 * @param accessToken - Access token corrente
 * @param refreshToken - Refresh token corrente (per revoca lato server)
 * @returns void
 */
export async function logout(
  accessToken: string,
  refreshToken?: string | null
): Promise<void> {
  try {
    await fetch(`${BASE_URL}${AUTH_BASE}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(refreshToken ? { 'X-Refresh-Token': refreshToken } : {}),
      },
    });
  } catch (error) {
    // Ignora errori di logout - l'utente viene comunque disconnesso localmente
    debugWarn('Logout API error (ignored):', error);
  }
}

/**
 * Restituisce messaggio errore per status HTTP.
 */
function getErrorMessage(status: number): string {
  const messages: Record<number, string> = {
    400: 'Dati non validi',
    401: 'Credenziali non valide',
    403: 'Account non attivo o bloccato',
    404: 'Risorsa non trovata',
    429: 'Troppe richieste. Riprova più tardi.',
    500: 'Errore del server',
  };
  return messages[status] || 'Errore sconosciuto';
}

export const authService = {
  login,
  refresh,
  logout,
  forgotPassword,
  resetPassword,
  changePassword,
};
